import { useState, useRef } from 'react';
import { createPatient } from './API';
import BottomSheet from './BottomSheet/BottomSheet';
import '../styles/CreatePatientModal.css';

const EMPTY_PATIENT = { email: '', firstName: '', lastName: '', dob: '', phone: '' };

export default function CreatePatientModal({ institution, onClose, onCreated }) {
  const [mode, setMode] = useState('single'); // 'single' | 'bulk'
  const [patient, setPatient] = useState({ ...EMPTY_PATIENT });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Bulk state
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkDone, setBulkDone] = useState(false);
  const bulkAbort = useRef(false);
  const [csvText, setCsvText] = useState('');

  // Inline editing state
  const [editingRow, setEditingRow] = useState(null); // row index or null
  const [editValue, setEditValue] = useState('');
  const editRef = useRef(null);

  function updateField(field, value) {
    setPatient(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  }

  // ── Single patient submit ──
  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    const missing = [];
    if (!patient.email) missing.push('email');
    if (!patient.firstName) missing.push('first name');
    if (!patient.lastName) missing.push('last name');
    if (!patient.dob) missing.push('date of birth');
    if (!patient.phone) missing.push('phone');
    if (missing.length > 0) {
      setError(`Missing: ${missing.join(', ')}`);
      return;
    }

    if (!/^\d{10}$/.test(patient.phone.replace(/\D/g, ''))) {
      setError('Phone must be exactly 10 digits');
      return;
    }

    setSubmitting(true);
    try {
      const result = await createPatient({
        ...patient,
        phone: patient.phone.replace(/\D/g, ''),
        institution,
      });
      setSuccess(`${result.patientName} created. Activation email sent.`);
      setPatient({ ...EMPTY_PATIENT });
      onCreated?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Row validation ──
  function validateRow(row) {
    if (!row.email || !row.firstName || !row.lastName || !row.dob || !row.phone) {
      return { valid: false, error: 'Missing fields' };
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
      return { valid: false, error: 'Invalid email' };
    } else if (!/^\d{10}$/.test(row.phone)) {
      return { valid: false, error: 'Invalid phone (need 10 digits)' };
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(row.dob)) {
      return { valid: false, error: 'Invalid DOB (need YYYY-MM-DD)' };
    }
    return { valid: true, error: null };
  }

  function parseRowFromCsv(line, id) {
    const cols = line.split(/[,\t]/).map(c => c.trim());
    const row = {
      id,
      email: cols[0] || '',
      firstName: cols[1] || '',
      lastName: cols[2] || '',
      dob: cols[3] || '',
      phone: (cols[4] || '').replace(/\D/g, ''),
    };
    return { ...row, ...validateRow(row) };
  }

  function rowToCsv(row) {
    return `${row.email},${row.firstName},${row.lastName},${row.dob},${row.phone}`;
  }

  // ── Bulk CSV parsing ──
  function handleBulkPaste(text) {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return;

    // Detect header row
    const firstLine = lines[0].toLowerCase();
    const hasHeader = firstLine.includes('email') || firstLine.includes('firstname');
    const dataLines = hasHeader ? lines.slice(1) : lines;

    const rows = dataLines.map((line, i) => parseRowFromCsv(line, i));

    setBulkRows(rows);
    setBulkResults([]);
    setBulkDone(false);
  }

  // ── Inline row editing ──
  function startEditing(index) {
    if (bulkRunning || bulkDone) return;
    const row = bulkRows[index];
    setEditingRow(index);
    setEditValue(rowToCsv(row));
    setTimeout(() => editRef.current?.focus(), 0);
  }

  function commitEdit() {
    if (editingRow === null) return;
    const updated = [...bulkRows];
    updated[editingRow] = parseRowFromCsv(editValue, editingRow);
    setBulkRows(updated);
    setEditingRow(null);
    setEditValue('');
  }

  function handleEditKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      setEditingRow(null);
      setEditValue('');
    }
  }

  function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => handleBulkPaste(ev.target.result);
    reader.readAsText(file);
  }

  // ── Bulk submit with concurrency pool ──
  async function handleBulkSubmit() {
    const validRows = bulkRows.filter(r => r.valid);
    if (validRows.length === 0) return;

    setBulkRunning(true);
    setBulkDone(false);
    bulkAbort.current = false;

    const results = bulkRows.map(r =>
      r.valid ? { ...r, status: 'pending' } : { ...r, status: 'skipped' }
    );
    setBulkResults([...results]);

    const CONCURRENCY = 3;
    let queue = validRows.map(r => r.id);
    let active = 0;

    await new Promise(resolve => {
      function next() {
        if (bulkAbort.current || (queue.length === 0 && active === 0)) {
          resolve();
          return;
        }

        while (active < CONCURRENCY && queue.length > 0) {
          const rowId = queue.shift();
          active++;
          const row = bulkRows.find(r => r.id === rowId);

          results[rowId] = { ...results[rowId], status: 'running' };
          setBulkResults([...results]);

          createPatient({
            email: row.email,
            firstName: row.firstName,
            lastName: row.lastName,
            dob: row.dob,
            phone: row.phone,
            institution,
          })
            .then(() => {
              results[rowId] = { ...results[rowId], status: 'success' };
            })
            .catch(err => {
              results[rowId] = { ...results[rowId], status: 'error', error: err.message };
            })
            .finally(() => {
              active--;
              setBulkResults([...results]);
              next();
            });
        }
      }
      next();
    });

    setBulkRunning(false);
    setBulkDone(true);
    onCreated?.();
  }

  function handleCancel() {
    bulkAbort.current = true;
  }

  const bulkSummary = bulkDone ? {
    success: bulkResults.filter(r => r.status === 'success').length,
    failed: bulkResults.filter(r => r.status === 'error').length,
    skipped: bulkResults.filter(r => r.status === 'skipped').length,
  } : null;

  return (
    <BottomSheet onClose={onClose} className="cpm-modal" zIndex={1000}>
      {({ onClose: handleClose }) => (
        <>
          <div className="cpm-header">
            <h2>Add Patient</h2>
            <button className="cpm-close" onClick={handleClose}>&times;</button>
          </div>

          <div className="cpm-tabs">
            <button
              className={mode === 'single' ? 'active' : ''}
              onClick={() => setMode('single')}
              disabled={bulkRunning}
            >
              Single
            </button>
            <button
              className={mode === 'bulk' ? 'active' : ''}
              onClick={() => setMode('bulk')}
              disabled={bulkRunning}
            >
              Bulk Import
            </button>
          </div>

          {mode === 'single' && (
            <form className="cpm-form" onSubmit={handleSubmit}>
              <div className="cpm-row">
                <label>Email</label>
                <input
                  type="email"
                  value={patient.email}
                  onChange={e => updateField('email', e.target.value)}
                  placeholder="patient@example.com"
                />
              </div>
              <div className="cpm-row-pair">
                <div className="cpm-row">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={patient.firstName}
                    onChange={e => updateField('firstName', e.target.value)}
                  />
                </div>
                <div className="cpm-row">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={patient.lastName}
                    onChange={e => updateField('lastName', e.target.value)}
                  />
                </div>
              </div>
              <div className="cpm-row-pair">
                <div className="cpm-row">
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    value={patient.dob}
                    onChange={e => updateField('dob', e.target.value)}
                  />
                </div>
                <div className="cpm-row">
                  <label>Phone (10 digits)</label>
                  <input
                    type="tel"
                    value={patient.phone}
                    onChange={e => updateField('phone', e.target.value)}
                    placeholder="5551234567"
                    maxLength="10"
                  />
                </div>
              </div>
              <div className="cpm-row">
                <label>Institution</label>
                <input type="text" value={institution} disabled />
              </div>

              {error && <div className="cpm-error">{error}</div>}
              {success && <div className="cpm-success">{success}</div>}

              <button type="submit" className="cpm-submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Patient'}
              </button>
            </form>
          )}

          {mode === 'bulk' && (
            <div className="cpm-bulk">
              {bulkRows.length === 0 && !bulkRunning && (
                <>
                  <p className="cpm-bulk-hint">
                    Paste CSV or upload a file. Columns: email, firstName, lastName, dob (YYYY-MM-DD), phone (10 digits)
                  </p>
                  <textarea
                    className="cpm-csv-input"
                    placeholder="email,firstName,lastName,dob,phone&#10;jane@example.com,Jane,Doe,1990-03-15,5551234567"
                    rows={6}
                    value={csvText}
                    onChange={e => setCsvText(e.target.value)}
                    onBlur={() => { if (csvText.trim()) handleBulkPaste(csvText); }}
                    onKeyDown={e => {
                      if (e.key === 'Tab' && csvText.trim()) {
                        e.preventDefault();
                        handleBulkPaste(csvText);
                      } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && csvText.trim()) {
                        e.preventDefault();
                        handleBulkPaste(csvText);
                      }
                    }}
                  />
                  <div className="cpm-file-upload">
                    <label>
                      Or upload CSV:
                      <input type="file" accept=".csv,.txt" onChange={handleFileUpload} />
                    </label>
                  </div>
                </>
              )}

              {bulkRows.length > 0 && (
                <>
                  <div className="cpm-bulk-table-wrapper">
                    <table className="cpm-bulk-table">
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Email</th>
                          <th>Name</th>
                          <th>DOB</th>
                          <th>Phone</th>
                          <th>Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(bulkResults.length > 0 ? bulkResults : bulkRows).map((row, i) => (
                          editingRow === i ? (
                            <tr key={i} className="bulk-row-editing">
                              <td colSpan={6}>
                                <input
                                  ref={editRef}
                                  className="cpm-inline-edit"
                                  value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  onKeyDown={handleEditKeyDown}
                                  onBlur={commitEdit}
                                  spellCheck={false}
                                />
                              </td>
                            </tr>
                          ) : (
                            <tr
                              key={i}
                              className={`bulk-row-${row.status || (row.valid ? 'ready' : 'invalid')}${!bulkRunning && !bulkDone ? ' bulk-row-editable' : ''}`}
                              onClick={() => startEditing(i)}
                            >
                              <td className="status-cell">
                                {row.status === 'success' && <span className="status-icon ok">&#10003;</span>}
                                {row.status === 'error' && <span className="status-icon fail">&#10007;</span>}
                                {row.status === 'running' && <span className="status-icon spin">&#8987;</span>}
                                {row.status === 'pending' && <span className="status-icon">&#8226;</span>}
                                {row.status === 'skipped' && <span className="status-icon skip">&#8212;</span>}
                                {!row.status && (row.valid ? <span className="status-icon">&#8226;</span> : <span className="status-icon fail">&#10007;</span>)}
                              </td>
                              <td>{row.email}</td>
                              <td>{row.firstName} {row.lastName}</td>
                              <td>{row.dob}</td>
                              <td>{row.phone}</td>
                              <td className="result-cell">{row.error || ''}</td>
                            </tr>
                          )
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {bulkSummary && (
                    <div className="cpm-bulk-summary">
                      <span className="summary-ok">{bulkSummary.success} created</span>
                      {bulkSummary.failed > 0 && <span className="summary-fail">{bulkSummary.failed} failed</span>}
                      {bulkSummary.skipped > 0 && <span className="summary-skip">{bulkSummary.skipped} skipped</span>}
                    </div>
                  )}

                  <div className="cpm-bulk-actions">
                    {!bulkRunning && !bulkDone && (
                      <>
                        <button
                          className="cpm-submit"
                          onClick={handleBulkSubmit}
                          disabled={bulkRows.filter(r => r.valid).length === 0}
                        >
                          Create {bulkRows.filter(r => r.valid).length} Patient{bulkRows.filter(r => r.valid).length !== 1 ? 's' : ''}
                        </button>
                        <button className="cpm-secondary" onClick={() => { setBulkRows([]); setBulkResults([]); setCsvText(''); }}>
                          Clear
                        </button>
                      </>
                    )}
                    {bulkRunning && (
                      <button className="cpm-cancel" onClick={handleCancel}>Cancel</button>
                    )}
                    {bulkDone && (
                      <button className="cpm-secondary" onClick={() => { setBulkRows([]); setBulkResults([]); setBulkDone(false); setCsvText(''); }}>
                        New Import
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </BottomSheet>
  );
}
